import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import "./i18n";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,
  });
}

document.documentElement.classList.remove("dark");
localStorage.removeItem("theme");

function reportClientError(message: string, extras?: Record<string, unknown>) {
  try {
    fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ message, url: window.location.href, ...extras }),
    }).catch((err) => { console.warn("[main] Failed to send client error report:", err); });
  } catch (err) { console.warn("[main] Error while preparing client error report:", err); }
}

window.onerror = (message, source, lineno, colno, error) => {
  if (error) {
    Sentry.captureException(error, {
      extra: { source, lineno, colno },
    });
  } else {
    Sentry.captureMessage(
      typeof message === "string" ? message : "Unknown error",
      { extra: { source, lineno, colno } },
    );
  }
  reportClientError(
    typeof message === "string" ? message : "Unknown error",
    { stack: error?.stack?.slice(0, 2000), source, lineno, colno },
  );
};

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  if (reason instanceof Error) {
    Sentry.captureException(reason);
  } else {
    Sentry.captureMessage(message, { extra: { type: "unhandledrejection" } });
  }
  reportClientError(message, {
    stack: reason instanceof Error ? reason.stack?.slice(0, 2000) : undefined,
    type: "unhandledrejection",
  });
};

createRoot(document.getElementById("root")!).render(<App />);
